// Research tree — 3 branches, 30 techs (5 tiers each, 2 techs per tier per branch)
// Prerequisites: tier 2+ requires at least one tech from the previous tier in the same branch.
// Effects are checked by ID in the tick loop / store.

export const RESEARCH_BRANCHES = [
  {
    id: "engineering",
    label: "Engineering",
    color: "#B042FF",
    icon: "Cog",
    desc: "Power, storage, and construction efficiency",
  },
  {
    id: "bio",
    label: "Bio Sciences",
    color: "#00FA9A",
    icon: "Wheat",
    desc: "Food, water, and colonist health",
  },
  {
    id: "aerospace",
    label: "Aerospace",
    color: "#00E5FF",
    icon: "Rocket",
    desc: "Cargo, oxygen, and storm resilience",
  },
];

export const TECHS = [
  // ============================================================
  // --- ENGINEERING BRANCH ---
  // ============================================================
  // Tier 1
  {
    id: "eng_solar2",
    branch: "engineering",
    tier: 1,
    name: "Advanced Solar Cells",
    desc: "Solar panels produce +50% power during daylight.",
    cost: { credits: 60, machine_parts: 10 },
    researchTime: 30, // game-seconds at 1x
  },
  {
    id: "eng_battery1",
    branch: "engineering",
    tier: 1,
    name: "High-Capacity Batteries",
    desc: "Battery storage capacity increased by +75%.",
    cost: { credits: 75, machine_parts: 12 },
    researchTime: 30,
  },
  // Tier 2
  {
    id: "eng_storage2",
    branch: "engineering",
    tier: 2,
    name: "Compact Storage",
    desc: "All storage buildings gain +50% capacity.",
    cost: { credits: 100, machine_parts: 20, polymers: 10 },
    researchTime: 50,
    requires: ["eng_solar2"],
  },
  {
    id: "eng_heat1",
    branch: "engineering",
    tier: 2,
    name: "Thermal Heat Sink",
    desc: "Improved thermal heat sinks boost all power generator output by 25%.",
    cost: { credits: 120, machine_parts: 18, polymers: 10 },
    researchTime: 50,
    requires: ["eng_battery1"],
  },
  // Tier 3
  {
    id: "eng_drones2",
    branch: "engineering",
    tier: 3,
    name: "Drone Swarm II",
    desc: "Construction drones build 2x faster.",
    cost: { credits: 150, machine_parts: 30, polymers: 20 },
    researchTime: 70,
    requires: ["eng_storage2"],
  },
  {
    id: "eng_reactor1",
    branch: "engineering",
    tier: 3,
    name: "Nuclear Power Tuning",
    desc: "Nuclear generators produce +40% more power.",
    cost: { credits: 180, machine_parts: 35, polymers: 18 },
    researchTime: 70,
    requires: ["eng_heat1"],
  },
  // Tier 4
  {
    id: "eng_repair1",
    branch: "engineering",
    tier: 4,
    name: "Automated Repair Arrays",
    desc: "Colony buildings auto-repair passive wear 50% faster.",
    cost: { credits: 280, machine_parts: 45, polymers: 25 },
    researchTime: 100,
    requires: ["eng_drones2"],
  },
  {
    id: "eng_mfg1",
    branch: "engineering",
    tier: 4,
    name: "Advanced Manufacturing",
    desc: "Parts factories and polymer plants produce +35% faster.",
    cost: { credits: 320, machine_parts: 50, polymers: 28 },
    researchTime: 100,
    requires: ["eng_reactor1"],
  },
  // Tier 5
  {
    id: "eng_fusion1",
    branch: "engineering",
    tier: 5,
    name: "Fusion Reactor Grid",
    desc: "Increases power grid output across all power facilities by +100%.",
    cost: { credits: 450, machine_parts: 70, polymers: 40 },
    researchTime: 140,
    requires: ["eng_repair1"],
  },
  {
    id: "eng_nanobots1",
    branch: "engineering",
    tier: 5,
    name: "Nanite Construction",
    desc: "Nanite swarms reduce building construct times by 60%.",
    cost: { credits: 490, machine_parts: 78, polymers: 48 },
    researchTime: 140,
    requires: ["eng_mfg1"],
  },

  // ============================================================
  // --- BIO SCIENCES BRANCH ---
  // ============================================================
  // Tier 1
  {
    id: "bio_hydro2",
    branch: "bio",
    tier: 1,
    name: "Hydroponic Yield",
    desc: "Greenhouses produce +100% food and no longer require a worker.",
    cost: { credits: 60, machine_parts: 8 },
    researchTime: 30,
  },
  {
    id: "bio_soil1",
    branch: "bio",
    tier: 1,
    name: "Enriched Regolith Soil",
    desc: "Soil organic treatment increases greenhouse crop growth rate by +30%.",
    cost: { credits: 70, machine_parts: 10 },
    researchTime: 30,
  },
  // Tier 2
  {
    id: "bio_medical",
    branch: "bio",
    tier: 2,
    name: "Medical Training",
    desc: "Colonists lose health 50% slower from resource shortages.",
    cost: { credits: 100, machine_parts: 15, polymers: 10 },
    researchTime: 50,
    requires: ["bio_hydro2"],
  },
  {
    id: "bio_recycle1",
    branch: "bio",
    tier: 2,
    name: "Waste Recycling",
    desc: "Reclaims +25% water and nutrients from colonist waste systems.",
    cost: { credits: 125, machine_parts: 20, polymers: 10 },
    researchTime: 50,
    requires: ["bio_soil1"],
  },
  // Tier 3
  {
    id: "bio_water2",
    branch: "bio",
    tier: 3,
    name: "Water Filtration",
    desc: "Water extractors produce +50% water; deposits deplete 50% slower.",
    cost: { credits: 150, machine_parts: 25, polymers: 15 },
    researchTime: 70,
    requires: ["bio_medical"],
  },
  {
    id: "bio_foodproc1",
    branch: "bio",
    tier: 3,
    name: "Nutrient Processing",
    desc: "Enhanced food rations reduce colonist food consumption by 25%.",
    cost: { credits: 175, machine_parts: 28, polymers: 18 },
    researchTime: 70,
    requires: ["bio_recycle1"],
  },
  // Tier 4
  {
    id: "bio_life1",
    branch: "bio",
    tier: 4,
    name: "Closed-Loop Life Support",
    desc: "Habitat oxygen and water consumption reduced by 35%.",
    cost: { credits: 270, machine_parts: 42, polymers: 26 },
    researchTime: 100,
    requires: ["bio_water2"],
  },
  {
    id: "bio_health1",
    branch: "bio",
    tier: 4,
    name: "Bio-Adaptive Immunity",
    desc: "Colonist health recovery in medical bays improved by +50%.",
    cost: { credits: 310, machine_parts: 48, polymers: 30 },
    researchTime: 100,
    requires: ["bio_foodproc1"],
  },
  // Tier 5
  {
    id: "bio_engineering1",
    branch: "bio",
    tier: 5,
    name: "Genetic Crop Synthesis",
    desc: "Genetically engineered crops increase greenhouse harvest yield by +80%.",
    cost: { credits: 440, machine_parts: 65, polymers: 38 },
    researchTime: 140,
    requires: ["bio_life1"],
  },
  {
    id: "bio_ecosystem1",
    branch: "bio",
    tier: 5,
    name: "Self-Sustaining Biosphere",
    desc: "Closed ecological loops reduce overall habitat resource demands by 50%.",
    cost: { credits: 480, machine_parts: 75, polymers: 45 },
    researchTime: 140,
    requires: ["bio_health1"],
  },

  // ============================================================
  // --- AEROSPACE BRANCH ---
  // ============================================================
  // Tier 1
  {
    id: "aero_cargo2",
    branch: "aerospace",
    tier: 1,
    name: "Cargo Boost",
    desc: "All cargo payloads deliver +50% resources.",
    cost: { credits: 60, machine_parts: 10 },
    researchTime: 30,
  },
  {
    id: "aero_comm1",
    branch: "aerospace",
    tier: 1,
    name: "Orbital Array Comms",
    desc: "Satellite comms increase supply shuttle arrival speed by +25%.",
    cost: { credits: 80, machine_parts: 12 },
    researchTime: 30,
  },
  // Tier 2
  {
    id: "aero_oxygen2",
    branch: "aerospace",
    tier: 2,
    name: "Atmospheric Processing",
    desc: "Oxygen generators produce +50% output.",
    cost: { credits: 100, machine_parts: 18, polymers: 10 },
    researchTime: 50,
    requires: ["aero_cargo2"],
  },
  {
    id: "aero_landing1",
    branch: "aerospace",
    tier: 2,
    name: "Landing Pad Automation",
    desc: "Automated docking reduces rocket turnaround time and fuel cost by 30%.",
    cost: { credits: 110, machine_parts: 16, polymers: 10 },
    researchTime: 50,
    requires: ["aero_comm1"],
  },
  // Tier 3
  {
    id: "aero_storm",
    branch: "aerospace",
    tier: 3,
    name: "Storm Shielding",
    desc: "Dust storms only reduce solar to 50% instead of 15%.",
    cost: { credits: 150, machine_parts: 28, polymers: 20 },
    researchTime: 70,
    requires: ["aero_oxygen2"],
  },
  {
    id: "aero_fuel1",
    branch: "aerospace",
    tier: 3,
    name: "Synthetic Rocket Fuel",
    desc: "Fuel refinement efficiency increased by +40%.",
    cost: { credits: 190, machine_parts: 32, polymers: 18 },
    researchTime: 70,
    requires: ["aero_landing1"],
  },
  // Tier 4
  {
    id: "aero_atmo1",
    branch: "aerospace",
    tier: 4,
    name: "Atmospheric Scrubbing",
    desc: "Ambient atmospheric scrubbers boost colony O2 generation by +60%.",
    cost: { credits: 290, machine_parts: 44, polymers: 27 },
    researchTime: 100,
    requires: ["aero_storm"],
  },
  {
    id: "aero_sat1",
    branch: "aerospace",
    tier: 4,
    name: "Deep Space Satellites",
    desc: "Orbital weather radar provides +100% advance dust storm warning.",
    cost: { credits: 330, machine_parts: 50, polymers: 30 },
    researchTime: 100,
    requires: ["aero_fuel1"],
  },
  // Tier 5
  {
    id: "aero_terra1",
    branch: "aerospace",
    tier: 5,
    name: "Atmospheric Terraforming",
    desc: "Thickens Mars atmosphere to double total colony oxygen output (+100%).",
    cost: { credits: 460, machine_parts: 72, polymers: 42 },
    researchTime: 140,
    requires: ["aero_atmo1"],
  },
  {
    id: "aero_orbital1",
    branch: "aerospace",
    tier: 5,
    name: "Orbital Supply Drops",
    desc: "Automated orbital drops deliver bonus supply payloads every Sol.",
    cost: { credits: 500, machine_parts: 80, polymers: 50 },
    researchTime: 140,
    requires: ["aero_sat1"],
  },
];

// Helper: get techs that are unlocked (prerequisites met, not yet completed)
export function getAvailableTechs(completed = []) {
  return TECHS.filter((t) => {
    if (completed.includes(t.id)) return false;
    if (!t.requires) return true;
    return t.requires.every((r) => completed.includes(r));
  });
}

// Helper: get techs in a branch, ordered by tier
export function getBranchTechs(branchId) {
  return TECHS.filter((t) => t.branch === branchId).sort((a, b) => a.tier - b.tier);
}

// Helper: check if a tech is unlocked (prerequisites met)
export function isTechUnlocked(tech, completed = []) {
  if (!tech.requires) return true;
  return tech.requires.every((r) => completed.includes(r));
}

// ──────────────────────────────────────────────
// Building unlock system
// ──────────────────────────────────────────────
// Maps building categories to research branches.
// A building with unlockRequirement.tier N and locked: true becomes available
// when at least one tech at tier N from the matching branch is completed.

export const BUILDING_BRANCH_MAP = {
  core:            "engineering",
  power:           "engineering",
  mining:          "engineering",
  manufacturing:   "engineering",
  storage:         "engineering",
  drones:          "engineering",
  habitation:      "bio",
  medical:         "bio",
  life:            "bio",
  spaceport:       "aerospace",
  communications:  "aerospace",
  expansion:       "aerospace",
  research:        "aerospace",  // research labs use aerospace tech
};

// Check if a building is unlocked given completed research techs
// building: the EM2C_BUILDINGS entry with .locked, .unlockRequirement, .category
// completed: array of completed tech ID strings
export function isBuildingUnlocked(building, completed = []) {
  // Not locked → always available
  if (!building.locked) return true;

  const reqTier = building.unlockRequirement?.tier;
  if (reqTier == null) return !building.locked;

  // Find which research branch governs this building's category
  const branch = BUILDING_BRANCH_MAP[building.category];
  if (!branch) {
    // No branch mapping → fallback: unlock if any tech at this tier is completed
    return TECHS.some(t => t.tier === reqTier && completed.includes(t.id));
  }

  // Unlock if at least one tech from the matching branch at this tier is completed
  return TECHS.some(
    t => t.branch === branch && t.tier === reqTier && completed.includes(t.id)
  );
}

// Get the tech that unlocks a specific building (for display in the build menu)
export function getUnlockTech(building) {
  if (!building.locked) return null;
  const reqTier = building.unlockRequirement?.tier;
  if (reqTier == null) return null;
  const branch = BUILDING_BRANCH_MAP[building.category];
  if (!branch) {
    return TECHS.find(t => t.tier === reqTier) || null;
  }
  return TECHS.find(t => t.branch === branch && t.tier === reqTier) || null;
}
